import React, { useState, useEffect } from 'react';

const API_URL = 'https://groovie-business-api-production.up.railway.app';

export default function LicenseAgreement() {
  const [agreementText, setAgreementText] = useState('');
  const [status, setStatus] = useState(null);
  const [signing, setSigning] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [signerEmail, setSignerEmail] = useState('');
  const [signerTitle, setSignerTitle] = useState('');
  const [devices, setDevices] = useState([]);
  const [complianceLogs, setComplianceLogs] = useState([]);
  const [activeTab, setActiveTab] = useState('agreement');
  const [error, setError] = useState('');

  const token = localStorage.getItem('token');
  const companyId = (() => {
    try { return JSON.parse(atob(token.split('.')[1])).company_id; }
    catch { return null; }
  })();
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    fetchAgreement();
    if (companyId) {
      fetchStatus();
      fetchDevices();
      fetchCompliance();
    }
  }, []);

  const fetchAgreement = async () => {
    try {
      const res = await fetch(`${API_URL}/api/v1/licenses/agreement/text`);
      const data = await res.json();
      console.log('Agreement response:', data);
      setAgreementText(data.text || data.agreement_text || '');
    } catch (e) { console.error('Agreement fetch failed:', e); }
  };

  const fetchStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/v1/licenses/agreement/status/${companyId}`, { headers });
      const data = await res.json();
      console.log('Status response:', data);
      setStatus(data);
    } catch (e) { console.error('Status fetch failed:', e); }
  };

  const fetchDevices = async () => {
    try {
      const res = await fetch(`${API_URL}/api/v1/licenses/devices/${companyId}`, { headers });
      const data = await res.json();
      setDevices(data.devices || []);
    } catch (e) {}
  };

  const fetchCompliance = async () => {
    try {
      const res = await fetch(`${API_URL}/api/v1/licenses/compliance/${companyId}`, { headers });
      const data = await res.json();
      setComplianceLogs(data.logs || []);
    } catch (e) {}
  };

  const handleSign = async () => {
    if (!signerName || !signerEmail || !signerTitle) { setError('Please fill in all fields.'); return; }
    setError('');
    setSigning(true);
    try {
      const res = await fetch(`${API_URL}/api/v1/licenses/agreement/sign/${companyId}`, {
        method: 'POST', headers,
        body: JSON.stringify({ signed_by_name: signerName, signed_by_email: signerEmail, signed_by_title: signerTitle })
      });
      const data = await res.json();
      console.log('Sign response:', data);
      if (res.ok) {
        await fetchStatus();
        await fetchCompliance();
      } else {
        setError(typeof data.detail === 'string' ? data.detail : 'Failed to sign. Please try again.');
      }
    } catch (e) { setError('Network error. Please try again.'); }
    setSigning(false);
  };

  const handleDeactivateDevice = async (deviceId) => {
    if (!window.confirm('Deactivate this device?')) return;
    try {
      await fetch(`${API_URL}/api/v1/licenses/devices/${deviceId}`, { method: 'DELETE', headers });
      fetchDevices();
    } catch (e) {}
  };

  const printLicense = () => {
    const a = status?.agreement;
    if (!a || !agreementText) return;
    const w = window.open('', '_blank');
    w.document.write(`<!DOCTYPE html><html><head><title>Groovie Music License</title>
<style>
body{font-family:Georgia,'Times New Roman',serif;max-width:800px;margin:0 auto;padding:40px;color:#111;line-height:1.6}
.hdr{text-align:center;border-bottom:3px solid #111;padding-bottom:20px;margin-bottom:30px}
.hdr h1{font-size:28px;margin:0;letter-spacing:2px}
.hdr p{color:#666;margin:4px 0;font-size:14px}
.badge{display:inline-block;background:#059669;color:#fff;padding:4px 16px;border-radius:4px;font-size:13px;font-weight:bold;margin-top:12px;font-family:Arial,sans-serif}
.meta{background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;padding:20px;margin-bottom:30px}
.mg{display:grid;grid-template-columns:1fr 1fr;gap:12px}
.mi{font-size:14px}
.mi strong{display:block;color:#374151;font-size:12px;text-transform:uppercase;letter-spacing:.5px;margin-bottom:2px}
.atxt{white-space:pre-wrap;font-size:13px;line-height:1.8;border:1px solid #e5e7eb;padding:24px;border-radius:8px}
.dsig{background:#ecfdf5;border:1px solid #a7f3d0;border-radius:6px;padding:12px 16px;margin-top:20px;font-family:Arial,sans-serif;font-size:13px}
.dsig strong{color:#059669}
.sl{margin-top:40px;display:flex;gap:60px}
.sb{flex:1}
.sb .ln{border-top:1px solid #111;margin-top:40px;padding-top:6px;font-size:12px;color:#666}
.ft{margin-top:40px;padding-top:20px;border-top:2px solid #111;display:flex;justify-content:space-between;font-size:12px;color:#666}
.wm{text-align:center;color:#d1d5db;font-size:10px;margin-top:30px}
@media print{body{padding:20px}}
</style></head><body>
<div class="hdr"><h1>GROOVIE</h1><p>Gooverio Labs LLC</p>
<p style="font-size:12px;color:#999">Autonomous Radio Platform • Music Licensing • Streaming Infrastructure</p>
<div class="badge">ACTIVE LICENSE</div></div>
<div class="meta"><div class="mg">
<div class="mi"><strong>License Holder</strong>${a.signed_by_name}</div>
<div class="mi"><strong>Title</strong>${a.signed_by_title}</div>
<div class="mi"><strong>Email</strong>${a.signed_by_email}</div>
<div class="mi"><strong>Date Signed</strong>${new Date(a.signed_at).toLocaleDateString('en-US',{year:'numeric',month:'long',day:'numeric'})}</div>
<div class="mi"><strong>Version</strong>${a.agreement_version||'1.0'}</div>
<div class="mi"><strong>Status</strong>Active</div>
</div></div>
<div class="atxt">${agreementText}</div>
<div class="dsig"><strong>Digitally Signed</strong> by ${a.signed_by_name} (${a.signed_by_email}) on ${new Date(a.signed_at).toLocaleString()}</div>
<div class="sl"><div class="sb"><div class="ln">Licensee Signature / ${a.signed_by_name}</div></div>
<div class="sb"><div class="ln">Date / ${new Date(a.signed_at).toLocaleDateString()}</div></div></div>
<div class="ft"><span>© 2026 Gooverio Labs LLC. All rights reserved.</span><span>Valid only for authorized locations.</span></div>
<div class="wm">Digitally generated by Groovie Business Platform. Verify at business.groovie.live</div>
<script>window.onload=function(){window.print()}</script></body></html>`);
    w.document.close();
  };

  const isSigned = status?.has_active_agreement === true || status?.signed === true;
  const agreement = status?.agreement;

  const sev = (s) => s === 'violation' ? '#fecaca' : s === 'warning' ? '#fef3c7' : '#f3f4f6';
  const sevTxt = (s) => s === 'violation' ? '#dc2626' : s === 'warning' ? '#d97706' : '#6b7280';

  return (
    <div style={{ padding: '32px 40px', maxWidth: 1100, margin: '0 auto', fontFamily: "'Outfit','DM Sans',sans-serif" }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 700, color: '#111827', margin: 0, letterSpacing: '-0.5px' }}>Music License</h1>
          <p style={{ fontSize: 14, color: '#9ca3af', marginTop: 4 }}>Commercial music public performance license — print and display at your location</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {isSigned && (
            <button onClick={printLicense} style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', borderRadius: 12,
              border: '1.5px solid #e5e7eb', background: '#fff', color: '#374151', fontSize: 14,
              fontWeight: 600, cursor: 'pointer', fontFamily: "'Outfit',sans-serif",
            }}>
              🖨️ Print / Download
            </button>
          )}
          <div style={{
            padding: '8px 18px', borderRadius: 10, fontSize: 13, fontWeight: 700,
            background: isSigned ? '#ecfdf5' : '#fffbeb',
            color: isSigned ? '#059669' : '#d97706',
            border: isSigned ? '1.5px solid #a7f3d0' : '1.5px solid #fde68a',
          }}>
            {isSigned ? '✓ License Active' : '⚠ Not Signed'}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', background: '#f3f4f6', borderRadius: 10, padding: 3, marginBottom: 24, width: 'fit-content' }}>
        {['agreement', 'devices', 'compliance'].map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={{
            padding: '8px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer',
            background: activeTab === tab ? '#fff' : 'transparent',
            color: activeTab === tab ? '#7c3aed' : '#6b7280',
            boxShadow: activeTab === tab ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
            textTransform: 'capitalize', fontFamily: "'Outfit',sans-serif",
          }}>{tab}</button>
        ))}
      </div>

      {/* Agreement Tab */}
      {activeTab === 'agreement' && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 16, padding: 28 }}>
          {/* Signed Banner */}
          {isSigned && agreement && (
            <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 12, padding: '14px 20px', marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#059669' }}>Signed by {agreement.signed_by_name}</p>
                <p style={{ margin: '2px 0 0', fontSize: 12, color: '#6b7280' }}>
                  {agreement.signed_by_email} · {agreement.signed_by_title} · {new Date(agreement.signed_at).toLocaleDateString()}
                </p>
              </div>
              <div style={{ padding: '4px 14px', background: '#059669', color: '#fff', borderRadius: 6, fontSize: 12, fontWeight: 700 }}>ACTIVE</div>
            </div>
          )}

          {/* Agreement Text */}
          <div style={{
            background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 12, padding: 24,
            maxHeight: 420, overflowY: 'auto', marginBottom: 24,
          }}>
            {agreementText ? (
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.7, color: '#374151', fontFamily: "'Outfit','DM Sans',sans-serif" }}>{agreementText}</pre>
            ) : (
              <p style={{ color: '#9ca3af', fontSize: 14 }}>Loading agreement text...</p>
            )}
          </div>

          {/* Sign Form — only if NOT signed */}
          {!isSigned && (
            <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 24 }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: '0 0 16px' }}>Sign Agreement</h3>
              {error && <p style={{ color: '#dc2626', fontSize: 13, marginBottom: 12, fontWeight: 600 }}>{error}</p>}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
                <input type="text" placeholder="Full Name" value={signerName} onChange={e => setSignerName(e.target.value)}
                  style={{ border: '1.5px solid #e5e7eb', borderRadius: 10, padding: '11px 14px', fontSize: 14, outline: 'none', fontFamily: "'Outfit',sans-serif" }} />
                <input type="email" placeholder="Email" value={signerEmail} onChange={e => setSignerEmail(e.target.value)}
                  style={{ border: '1.5px solid #e5e7eb', borderRadius: 10, padding: '11px 14px', fontSize: 14, outline: 'none', fontFamily: "'Outfit',sans-serif" }} />
                <input type="text" placeholder="Title (e.g. Owner, Manager)" value={signerTitle} onChange={e => setSignerTitle(e.target.value)}
                  style={{ border: '1.5px solid #e5e7eb', borderRadius: 10, padding: '11px 14px', fontSize: 14, outline: 'none', fontFamily: "'Outfit',sans-serif" }} />
              </div>
              <button onClick={handleSign} disabled={signing} style={{
                padding: '12px 28px', borderRadius: 12, border: 'none', fontSize: 14, fontWeight: 700,
                background: signing ? '#c4b5fd' : 'linear-gradient(135deg, #7c3aed, #a855f7)',
                color: '#fff', cursor: signing ? 'not-allowed' : 'pointer', fontFamily: "'Outfit',sans-serif",
                boxShadow: '0 4px 12px rgba(124,58,237,0.3)',
              }}>{signing ? 'Signing...' : 'I Agree — Sign License'}</button>
            </div>
          )}
        </div>
      )}

      {/* Devices Tab */}
      {activeTab === 'devices' && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 16, padding: 28 }}>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: '0 0 16px' }}>Licensed Devices ({devices.length})</h3>
          {devices.length === 0 ? (
            <p style={{ color: '#9ca3af', fontSize: 14 }}>No devices registered. Devices are auto-registered when locations start streaming.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {devices.map(d => (
                <div key={d.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 12, padding: '14px 18px' }}>
                  <div>
                    <p style={{ margin: 0, fontWeight: 600, color: '#111827', fontSize: 14 }}>{d.device_name || 'Unnamed Device'}</p>
                    <p style={{ margin: '2px 0 0', fontSize: 12, color: '#9ca3af' }}>
                      {d.location_name} · Token: {d.device_token?.slice(0, 12)}... · Last seen: {d.last_seen_at ? new Date(d.last_seen_at).toLocaleString() : 'Never'}
                    </p>
                  </div>
                  <button onClick={() => handleDeactivateDevice(d.id)} style={{
                    padding: '6px 14px', borderRadius: 8, border: '1.5px solid #fecaca', background: '#fff',
                    color: '#dc2626', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  }}>Deactivate</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Compliance Tab */}
      {activeTab === 'compliance' && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 16, padding: 28 }}>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: '0 0 16px' }}>Compliance Audit Log</h3>
          {complianceLogs.length === 0 ? (
            <p style={{ color: '#9ca3af', fontSize: 14 }}>No compliance events recorded yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 420, overflowY: 'auto' }}>
              {complianceLogs.map((log, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '10px 16px' }}>
                  <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: sev(log.severity), color: sevTxt(log.severity) }}>{log.severity}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#111827' }}>{(log.event_type || '').replace(/_/g, ' ')}</p>
                    <p style={{ margin: 0, fontSize: 11, color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.event_detail}</p>
                  </div>
                  <span style={{ fontSize: 11, color: '#9ca3af', whiteSpace: 'nowrap' }}>{new Date(log.created_at).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
